import { API_ENDPOINT, authHeaders, getAuthToken, parseApiResponse } from ".";

export interface OrderItem {
  id: number;
  quantity: number;
  unitPricePence: number;
  subtotalPence: number;
  ticketType: {
    id: number;
    name: string;
    description: string | null;
  };
  event: {
    id: number;
    title: string;
    slug: string;
  };
}

export interface OrderPayment {
  status: string;
  stripeInvoiceId: string | null;
  receiptUrl: string | null;
  invoiceAvailable: boolean;
  receiptPdfAvailable: boolean;
  ticketAvailable: boolean;
}

export interface Order {
  id: number;
  orderNumber: string | null;
  customerEmail: string;
  customerName: string | null;
  status: string;
  totalAmountPence: number;
  currency: string;
  stripeCheckoutSessionId: string | null;
  createdAt: string;
  updatedAt: string;
  orderItems: OrderItem[];
  registrationId: string | null;
  waivesCancellationAdminFee?: boolean;
  payment: OrderPayment | null;
}

export async function getMyOrders(): Promise<Order[]> {
  const response = await fetch(`${API_ENDPOINT}/orders/me`, {
    headers: authHeaders(),
  });
  const data = await parseApiResponse<{ orders: Order[] }>(response);
  return data.orders;
}

export async function getOrderBySession(sessionId: string): Promise<Order> {
  const response = await fetch(
    `${API_ENDPOINT}/orders/by-session/${encodeURIComponent(sessionId)}`,
  );
  const data = await parseApiResponse<{ order: Order }>(response);
  return data.order;
}

export async function downloadOrderInvoice(orderId: number): Promise<Blob> {
  const token = getAuthToken();
  const response = await fetch(`${API_ENDPOINT}/orders/${orderId}/invoice`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error("Failed to download invoice");
  }
  return response.blob();
}

export async function downloadInvoiceBySession(sessionId: string): Promise<Blob> {
  const response = await fetch(
    `${API_ENDPOINT}/orders/by-session/${encodeURIComponent(sessionId)}/invoice`,
  );
  if (!response.ok) {
    throw new Error("Failed to download invoice");
  }
  return response.blob();
}

export async function downloadOrderReceipt(orderId: number): Promise<Blob> {
  const response = await fetch(`${API_ENDPOINT}/orders/${orderId}/receipt`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to download receipt");
  }
  return response.blob();
}

export async function downloadReceiptBySession(sessionId: string): Promise<Blob> {
  const response = await fetch(
    `${API_ENDPOINT}/orders/by-session/${encodeURIComponent(sessionId)}/receipt`,
  );
  if (!response.ok) {
    throw new Error("Failed to download receipt");
  }
  return response.blob();
}

export function receiptFilename(order: {
  id: number;
  orderNumber: string | null;
  registrationId?: string | null;
}): string {
  const receiptNo =
    order.registrationId ??
    order.orderNumber ??
    `EH-${String(order.id).padStart(6, "0")}`;
  return `OFFICIAL PAYMENT RECEIPT ${receiptNo}.pdf`;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
